import { useRouter } from 'next/router';
import { FunctionalComponent } from 'interfaces/components';
import Container from 'components/Container';
import DropdownWithArrow from 'components/DropdownWithArrow';
import { H1, H4 } from 'components/Text';
import DownArrowIcon from 'assets/svgs/DownArrowIcon';
import NotificationIcon from 'assets/svgs/NotificationIcon';
import SettingsIcon from 'assets/svgs/SettingsIcon';
import TicketIcon from 'assets/svgs/TicketIcon';
import Layout from 'components/Layout';
import { useTranslation } from 'next-i18next/pages';
import { Menu } from '@headlessui/react';
import { findKeyByRewriteUrl } from 'urlTranslations';
import { PAGE_TOP, PAGE_BOTTOM } from 'components/TextPage/pageLayout';
import { useAuthContext } from 'context/AuthContext';

interface IProfileLayoutProps extends FunctionalComponent {
  titleProduction: string;
  titleStaging: string;
  metaDescriptionProd: string;
  metaDescriptionStaging: string;
  canonicalProd: string;
  canonicalStaging: string;
}

const ProfileLayout: React.FC<IProfileLayoutProps> = ({
  children,
  ...props
}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const {
    titleProduction,
    titleStaging,
    metaDescriptionProd,
    metaDescriptionStaging,
    canonicalProd,
    canonicalStaging,
  } = props;
  const { authUser: user } = useAuthContext();
  const renderIcon = (iconType: string, color?: string) => {
    switch (iconType) {
      case '/notifications':
        return <NotificationIcon className="w-6 h-6" color={color} />;
      case '/bookings':
        return <TicketIcon className="w-6 h-6" color={color} />;
      default:
        return <SettingsIcon className="w-6 h-6" color={color} />;
    }
  };

  const profileMenuItems = {
    '/settings/': {
      title: t('Settings'),
      icon: (color?: string) => renderIcon('/settings', color),
    },
    '/bookings/': {
      title: t('My bookings'),
      icon: (color?: string) => renderIcon('/bookings', color),
    },
    '/notifications/': {
      title: t('Notifications'),
      icon: (color?: string) => renderIcon('/notifications', color),
    },
  };
  // The menu keys end in a slash; router.pathname does not (WHIT-479).
  const currentKey = `${router.pathname}/` as keyof typeof profileMenuItems;

  const renderMenuItems = (isMobile?: boolean) =>
    Object.keys(profileMenuItems).map(el => {
      const keyOfMenu = el as keyof typeof profileMenuItems;
      if (keyOfMenu === '/settings/') {
        // Don't render the settings menu option if order id is present in the url and the user is anonymous
        const { id } = router.query;
        if (id && (!user?.uid || user?.isAnonymous)) {
          return null;
        }
      }
      return (
        <Menu.Item
          key={profileMenuItems[keyOfMenu].title}
          as="button"
          onClick={() => {
            const { id } = router.query;
            if (id && (!user?.uid || user?.isAnonymous)) {
              if (keyOfMenu === '/settings/') return;
              if (router.pathname + '/' === keyOfMenu) return;
            }
            return router.push(
              `${findKeyByRewriteUrl(keyOfMenu, router.locale)}${
                (!user?.uid || user?.isAnonymous) && id ? `?id=${id}` : ''
              }`,
            );
          }}
          type="button"
          className={`flex md:space-x-6 md:py-4 ${
            isMobile
              ? ''
              : keyOfMenu === currentKey
                ? 'border-r-2 border-r-tt-yellow'
                : ' border-r-2 border-r-tt-verylight-purple'
          }`}>
          {profileMenuItems[keyOfMenu].icon(
            keyOfMenu !== currentKey ? 'var(--tt-light-purple)' : undefined,
          )}
          <div className="flex items-center">
            <H4
              className={`${
                keyOfMenu !== currentKey ? 'text-tt-light-purple' : ''
              }`}>
              {profileMenuItems[keyOfMenu].title}
            </H4>
          </div>
        </Menu.Item>
      );
    });

  return (
    <Layout
      titleProduction={titleProduction}
      titleStaging={titleStaging}
      metaDescriptionProd={metaDescriptionProd}
      metaDescriptionStaging={metaDescriptionStaging}
      canonicalProd={canonicalProd}
      canonicalStaging={canonicalStaging}
      divClassName="lg:h-full lg:flex lg:flex-col"
      navBarClassName="bg-white py-6">
      <Container
        containerClassName="bg-tt-white-purple flex-1 z-0"
        className={`flex flex-col ${PAGE_TOP} ${PAGE_BOTTOM}`}>
        <div className="flex justify-between">
          <H1 className="dm-sans flex-1 w-full self-center !text-[1.125rem]">
            <b>{t('My profile')}</b>
          </H1>
          <div className="md:hidden">
            <DropdownWithArrow
              menuItemsClassName="text-sm text-gray-700 dark:text-gray-200 origin-top-right right-0 z-10 md:hidden p-4 space-y-6"
              label={
                <div className="flex space-x-4 items-center">
                  {renderIcon(router.pathname)}
                  <H4>{profileMenuItems[currentKey]?.title}</H4>
                </div>
              }
              dropDownArrowIcon={
                <DownArrowIcon
                  className="h-5 w-7 self-center ml-2"
                  color="var(--tt-yellow)"
                />
              }
              pointerCursor>
              {/* <div className="md:hidden p-4 space-y-6"> */}
              {renderMenuItems(true)}
              {/* </div> */}
            </DropdownWithArrow>
          </div>
        </div>
        <div className="flex flex-1 flex-col md:flex-row mt-4">
          <Menu
            as="div"
            className="hidden flex-[2_2_0%] md:flex space-x-4 w-full md:space-x-0 md:flex-col w-32 md:w-44 pt-2">
            {renderMenuItems()}
          </Menu>
          <div className="flex-[10_10_0%]">{children}</div>
        </div>
      </Container>
    </Layout>
  );
};

export default ProfileLayout;
